/**
 * BP Tracking Page - Main page for blood pressure tracking feature.
 *
 * Features:
 * - BP entry form
 * - Trend charts with date range selection
 * - Reading history with pagination
 * - Edit and delete functionality
 */

import React, { useState, useRef } from 'react';
import BPEntryForm from '../components/bp-tracking/BPEntryForm';
import BPTrendChart from '../components/bp-tracking/BPTrendChart';
import BPHistoryList from '../components/bp-tracking/BPHistoryList';
import type { BPReading } from '../types/bloodPressure';

const BPTrackingPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<number>(30);
  const formRef = useRef<HTMLDivElement>(null);

  const handleEdit = (_reading: BPReading) => {
    // Scroll to form when editing (reading data pre-populated automatically by BPEntryForm)
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-serif font-bold text-neutral-800 mb-2">
          Blood Pressure Tracking
        </h1>
        <p className="text-lg text-neutral-600">
          Monitor your blood pressure over time to support your cardiovascular health
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Entry Form */}
        <div className="lg:col-span-1">
          <div ref={formRef}>
            <BPEntryForm />
          </div>
        </div>

        {/* Right Column: Trends and History */}
        <div className="lg:col-span-2 space-y-8">
          {/* Date Range Selector */}
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-serif font-semibold text-neutral-800">
              Your BP Overview
            </h2>
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateRange(days)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    dateRange === days
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>

          {/* Trend Chart */}
          <BPTrendChart days={dateRange} />

          {/* History List */}
          <BPHistoryList
            days={dateRange}
            limit={20}
            onEdit={handleEdit}
          />
        </div>
      </div>
    </div>
  );
};

export default BPTrackingPage;
